import {
  Alert,
  Button,
  Card,
  CardHeader,
  CardBody,
  Select,
  SelectItem,
} from '@heroui/react';
import dayjs from 'dayjs';
import _ from 'lodash';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';

import Background from '@/assets/images/launch.png';
import useHospitalFlags from '@/hooks/useHospitalFlags';
import useSegment from '@/hooks/useSegment';
import { useAccountFormMutation } from '@/patient-app-common/api/authApi';
import { useAppSelector } from '@/redux/hooks';
import { FormField } from '@/patient-app-common/types/Auth';
import { getURLParam } from '@/patient-app-common/utils';

import { AccountForm } from './AccountForm';
import styles from './styles/login.module.css';

const EVENT = 'Signup Start button clicked';

export function Setup() {
  const location = useLocation();
  const { sendTrackEvent } = useSegment();
  const { accessKey, setupToken } = useParams<{
    accessKey: string;
    setupToken: string;
  }>();
  const { route } = useAppSelector(({ app }) => app);
  const { i18n, t } = useTranslation('login');
  const { hospital } = useHospitalFlags();

  const [error, setError] = useState(false);
  const [form, setForm] = useState<FormField[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [languageField, setLanguageField] = useState<null | FormField>(null);

  const [accountForm, { isLoading }] = useAccountFormMutation();

  const preselectLanguageField = (languages: FormField) => {
    const initLocale = getURLParam(location.search, 'lang');
    if (!initLocale) return languages;

    const preselectedLang = languages.options.find((lang) =>
      lang.includes(initLocale)
    );
    if (!preselectedLang) return languages;

    const langId = preselectedLang[1];

    return { ...languages, value: langId };
  };

  const getSetupForm = async () => {
    if (!accessKey) return;

    try {
      const results = await accountForm({ token: accessKey }).unwrap();

      if (results.success) {
        setForm(results.form);

        const languages = _.find(results.form, ['fieldCode', 'language_id']);
        if (languages) {
          setLanguageField(preselectLanguageField(languages));
        }
      } else {
        setError(true);
      }
    } catch (e) {
      console.error(e);
      setError(true);
    }
  };

  useEffect(() => {
    if (route !== null) {
      getSetupForm();
    }
  }, [route]);

  useEffect(() => {
    const initLocale = getURLParam(location.search, 'lang') || 'en';
    dayjs.locale(initLocale);
    i18n.changeLanguage(initLocale);
  }, []);

  const handleLocaleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (!languageField) return;

    const [, langId, locale] = e.target.value.split(',');
    const languageFieldCopy = { ...languageField };
    languageFieldCopy.value = langId;
    setLanguageField(languageFieldCopy);

    if (!locale) return;
    dayjs.locale(locale);
    i18n.changeLanguage(locale);
    document.documentElement.lang = locale;
  };

  const getFormWithSelectedLang = () => {
    const formCopy: FormField[] = [...form];

    if (!languageField?.value) return;

    _.remove(
      formCopy,
      (field) =>
        field.languageId && field.languageId !== Number(languageField.value)
    );
    _.remove(formCopy, (field) => field.fieldCode === 'language_id');

    const languageFieldCopy = { ...languageField };
    languageFieldCopy.visible = false;

    formCopy.push(languageFieldCopy);
    setForm(formCopy);
  };

  const handleSetupStart = () => {
    if (languageField) {
      getFormWithSelectedLang();
    }

    setShowForm(true);
    sendTrackEvent(EVENT);
  };

  if (showForm) {
    return (
      <div
        style={{
          backgroundImage: `url(${Background})`,
          backgroundSize: '100%',
          minHeight: '100dvh',
        }}
        className={styles.background}
      >
        <AccountForm jsonForm={form} token={setupToken} />
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundImage: `url(${Background})`,
        backgroundSize: '100%',
        minHeight: '100dvh',
      }}
      className={styles.background}
    >
      <div className="flex flex-col items-center justify-center h-[100dvh]">
        <div className="sm:max-w-xl w-full">
          <Card shadow="sm" className="border border-grey px-4">
            <CardHeader className="flex flex-col">
              {hospital?.logo && (
                <img
                  className={styles.logoContainer}
                  src={hospital.logo}
                  alt={hospital.displayName}
                />
              )}
              <h1 className="text-xl font-semibold">{t('setup.title')}</h1>
            </CardHeader>
            <CardBody className="gap-4">
              <p>{t('setup.description')}</p>
              <p>{t('setup.text_disclaimer')}</p>
              <Alert
                color="danger"
                hideIcon
                isVisible={error}
                radius="none"
                title={t('common.error.server')}
              />

              {!_.isEmpty(languageField) && (
                <Select
                  className="max-w-xs place-self-center"
                  label={t('common.select.language')}
                  onChange={(e) => handleLocaleChange(e)}
                >
                  {languageField.options.map(
                    (lang: [string, string, string]) => (
                      <SelectItem key={lang.toString()}>{lang[0]}</SelectItem>
                    )
                  )}
                </Select>
              )}

              <Button
                className="mx-auto w-1/2"
                color="primary"
                disabled={error}
                isLoading={isLoading}
                onPress={handleSetupStart}
              >
                {t('common.action.start')}
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
